import ApiService from "@/api/ApiService";
import { Alert, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, List, ListItem } from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { initialResourceOrder, ProjectState, ResourceOrder } from "../Project";
import { RealBookingRow, RealBookingStruct } from "../horizontalCalendarElements/BookingOrderCombiner";

type Extension = { phaseUuid: string; oldEnd: string; referenceDate: string; newEnd: string };
type ExtensionItem = {
  key: string;
  label: string;
  number?: string;
  italic: boolean;
  type: ResourceOrder["type"];
  orderIndex: number | null;
  booking: RealBookingRow | null;
};

const getExtensionReferenceDate = (oldEnd: string) => {
  const end = dayjs(oldEnd);
  if (end.day() === 6) return end.subtract(1, "day").format("YYYY-MM-DD");
  if (end.day() === 0) return end.subtract(2, "days").format("YYYY-MM-DD");
  return oldEnd;
};

/** Watches the common project state so extensions from the editor and both calendars use the same flow. */
export default function PhaseExtensionDialog({
  project,
  setProjectField,
}: {
  project: ProjectState;
  setProjectField: (changes: Partial<ProjectState>, createBreakpoint?: boolean) => void;
}) {
  const previousEnds = useRef<Map<string, string | null> | null>(null);
  const queue = useRef<Extension[]>([]);
  const [extension, setExtension] = useState<Extension | null>(null);
  const [stage, setStage] = useState<"question" | "inventory">("question");
  const [items, setItems] = useState<ExtensionItem[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [rejectedOrderIds, setRejectedOrderIds] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const tagNameQuery = useQuery<{ id: number; name: string }[], AxiosResponse>({
    queryKey: ["getTagNames", String(project.region)],
    queryFn: ApiService.getEntitiesWithFilter("resourceTag", { region: ["region", project.region] }, "names"),
  });
  const tagNames = useMemo(() => new Map((tagNameQuery.data ?? []).map((tag) => [tag.id, tag.name])), [tagNameQuery.data]);

  useEffect(() => {
    const current = new Map(project.phases.map((phase) => [phase.uuid, phase.plannedEnd]));
    if (previousEnds.current) {
      for (const phase of project.phases) {
        const oldEnd = previousEnds.current.get(phase.uuid);
        if (!phase.deleted && (phase.id ?? 0) > 0 && phase.autoFill && oldEnd && phase.plannedEnd && phase.plannedEnd > oldEnd) {
          queue.current.push({
            phaseUuid: phase.uuid,
            oldEnd,
            referenceDate: getExtensionReferenceDate(oldEnd),
            newEnd: phase.plannedEnd,
          });
        }
      }
      if (!extension && queue.current.length) setExtension(queue.current.shift()!);
    }
    previousEnds.current = current;
  }, [project.phases, extension]);

  const close = () => {
    setExtension(null);
    setStage("question");
    setItems([]);
    setRejectedOrderIds(new Set());
    setError(null);
    setTimeout(() => {
      if (queue.current.length) setExtension(queue.current.shift()!);
    });
  };

  const takeInventory = async () => {
    if (!extension) return;
    setLoading(true);
    setError(null);
    try {
      // The phase date/order changes have already reached project state before this request is made.
      const [bookings, rejections] = await Promise.all([
        ApiService.getAllRealBookings(project.id, {
          staff: false,
          humans: false,
          crews: true,
          resources: true,
        })() as Promise<RealBookingStruct>,
        ApiService.getProjectRejections(project.id)() as Promise<Record<string, Record<string, Record<number, unknown>>>>,
      ]);
      const phase = project.phases.find((candidate) => candidate.uuid === extension.phaseUuid)!;
      const oldJob = Object.values(phase.jobs).find((job) => !job.deleted && job.date === extension.referenceDate);
      const rejected = new Set(Object.keys(rejections[phase.uuid]?.[extension.referenceDate] ?? {}).map(Number));
      const dayBookings = (oldJob ? bookings[phase.uuid]?.[oldJob.uuid] ?? [] : []).filter(
        (booking) =>
          booking.type !== "HUMAN" &&
          booking.type !== "CREW_MEMBER" &&
          (booking.resource_order == null || !rejected.has(booking.resource_order)),
      );
      const validOrders = phase.resourceOrders
        .map((order, index) => ({ order, index }))
        .filter(({ order }) =>
          !order.deleted &&
          order.type !== "HUMAN" &&
          !rejected.has(order.id) &&
          (!order.startConstraint || order.startConstraint <= extension.referenceDate) &&
          (!order.endConstraint || order.endConstraint >= extension.referenceDate) &&
          (!oldJob || !order.exceptions.includes(oldJob.uuid)),
        );
      const bookingByOrder = new Map<number, RealBookingRow[]>();
      for (const booking of dayBookings) {
        if (booking.resource_order == null) continue;
        bookingByOrder.set(booking.resource_order, [...(bookingByOrder.get(booking.resource_order) ?? []), booking]);
      }
      const inventory: ExtensionItem[] = [];
      for (const { order, index } of validOrders) {
        const matches = bookingByOrder.get(order.id) ?? [];
        for (let slot = 0; slot < order.amount; slot++) {
          const booking = matches[slot] ?? null;
          inventory.push({
            key: `order-${index}-${slot}`,
            label: booking?.name ?? booking?.tagName ?? tagNames.get(order.tag ?? 0) ?? "Unbekannte Kategorie",
            number: booking?.type === "RESOURCE" ? booking.number : undefined,
            italic: !booking,
            type: order.type,
            orderIndex: index,
            booking,
          });
        }
      }
      for (const booking of dayBookings.filter((entry) => entry.resource_order == null)) {
        inventory.push({
          key: `booking-${booking.type}-${booking.id}`,
          label: booking.name,
          number: booking.type === "RESOURCE" ? booking.number : undefined,
          italic: false,
          type: booking.type === "CREW_MEMBER" ? "HUMAN" : booking.type,
          orderIndex: null,
          booking,
        });
      }
      const typeOrder: Record<ResourceOrder["type"], number> = { CREW: 0, HUMAN: 1, RESOURCE: 2 };
      inventory.sort((a, b) => typeOrder[a.type] - typeOrder[b.type] || a.label.localeCompare(b.label, "de", { sensitivity: "base" }));
      setItems(inventory);
      setSelected(new Set(inventory.map((item) => item.key)));
      setStage("inventory");
    } catch {
      setError("Die Ressourcen am bisherigen letzten Tag konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  const declineExtension = () => {
    if (!extension) return;
    const phaseIndex = project.phases.findIndex((candidate) => candidate.uuid === extension.phaseUuid);
    if (phaseIndex < 0) return close();
    const phases = project.phases.slice();
    const phase = phases[phaseIndex];
    phases[phaseIndex] = {
      ...phase,
      resourceOrders: phase.resourceOrders.map((order) =>
        !order.deleted &&
        (!order.startConstraint || order.startConstraint <= extension.oldEnd) &&
        (!order.endConstraint || order.endConstraint > extension.oldEnd)
          ? { ...order, endConstraint: extension.oldEnd, changed: true }
          : order,
      ),
    };
    setProjectField({ phases }, true);
    close();
  };

  const cancelInventory = () => {
    if (!extension || rejectedOrderIds.size === 0) return close();
    const phaseIndex = project.phases.findIndex((candidate) => candidate.uuid === extension.phaseUuid);
    if (phaseIndex < 0) return close();
    const phases = project.phases.slice();
    const phase = phases[phaseIndex];
    phases[phaseIndex] = {
      ...phase,
      resourceOrders: phase.resourceOrders.map((order) =>
        !order.deleted && 
        (rejectedOrderIds.has(order.id) || order.type === "HUMAN") && 
        (!order.startConstraint || order.startConstraint <= extension.oldEnd) &&
        (!order.endConstraint || order.endConstraint > extension.oldEnd)
          ? { ...order, endConstraint: extension.oldEnd, changed: true }
          : order,
      ),
    };
    setProjectField({ phases }, true);
    close();
  };

  const apply = () => {
    if (!extension) return;
    const phaseIndex = project.phases.findIndex((candidate) => candidate.uuid === extension.phaseUuid);
    if (phaseIndex < 0) return close();
    const phases = project.phases.slice();
    const phase = { ...phases[phaseIndex], resourceOrders: phases[phaseIndex].resourceOrders.slice() };
    phase.resourceOrders = phase.resourceOrders.map((order) =>
      !order.deleted &&
      (rejectedOrderIds.has(order.id) || order.type === "HUMAN") &&
      (!order.startConstraint || order.startConstraint <= extension.oldEnd) &&
      (!order.endConstraint || order.endConstraint > extension.oldEnd)
        ? { ...order, endConstraint: extension.oldEnd, changed: true }
        : order,
    );
    const selectionByOrder = new Map<number, boolean[]>();
    for (const item of items) {
      if (item.orderIndex == null) continue;
      selectionByOrder.set(item.orderIndex, [...(selectionByOrder.get(item.orderIndex) ?? []), selected.has(item.key)]);
    }
    for (const [index, choices] of selectionByOrder) {
      const order = phase.resourceOrders[index];
      // An order is atomic. Partial selection creates a constrained copy for the chosen slots.
      const selectedAmount = choices.filter(Boolean).length;
      if (selectedAmount === order.amount) {
        if (order.endConstraint && order.endConstraint <= extension.oldEnd) {
          phase.resourceOrders[index] = { ...order, endConstraint: extension.newEnd, changed: true };
        }
      } else {
        if (!order.endConstraint || order.endConstraint > extension.oldEnd) {
          phase.resourceOrders[index] = { ...order, endConstraint: extension.oldEnd, changed: true };
        }
        if (selectedAmount > 0) {
          phase.resourceOrders.push({
            ...order,
            id: 0,
            amount: selectedAmount,
            startConstraint: dayjs(extension.oldEnd).add(1, "day").format("YYYY-MM-DD"),
            endConstraint: extension.newEnd,
            references: items
              .filter((item) => item.orderIndex === index && selected.has(item.key) && item.booking)
              .map((item) => item.booking!.id),
            changed: true,
          });
        }
      }
    }
    for (const item of items.filter((candidate) => candidate.orderIndex == null && selected.has(candidate.key) && candidate.booking?.tag)) {
      const booking = item.booking!;
      if (booking.type !== "CREW" && booking.type !== "RESOURCE") continue;
      phase.resourceOrders.push({
        ...initialResourceOrder(),
        tag: booking.tag!,
        type: booking.type,
        startConstraint: dayjs(extension.oldEnd).add(1, "day").format("YYYY-MM-DD"),
        endConstraint: extension.newEnd,
        references: [booking.id],
        exceptions: [],
        changed: true,
      } as ResourceOrder & { exceptions: string[]; changed: boolean });
    }
    phases[phaseIndex] = phase;
    setProjectField({ phases }, true);
    close();
  };

  return (
    <Dialog open={!!extension} maxWidth="sm" fullWidth onClose={stage === "question" ? declineExtension : cancelInventory}>
      <DialogTitle>{stage === "question" ? "Ressourcen verlängern?" : "Ressourcen für die Verlängerung"}</DialogTitle>
      <DialogContent>
        {stage === "question" ? (
          <DialogContentText>Sollen die am bisherigen letzten Arbeitstag gebuchten und bestellten Ressourcen automatisch verlängert werden?</DialogContentText>
        ) : items.length ? (
          <List dense>
            {items.map((item) => (
              <ListItem key={item.key} disablePadding>
                <FormControlLabel
                  control={<Checkbox checked={selected.has(item.key)} onChange={(_, checked) => setSelected((old) => { const next = new Set(old); checked ? next.add(item.key) : next.delete(item.key); return next; })} />}
                  label={
                    <span style={{ fontStyle: item.italic ? "italic" : "normal" }}>
                      {item.number ? <span style={{ color: "#444", fontSize: "12px", marginRight: "3px" }}>{item.number}</span> : null}
                      {item.label}
                    </span>
                  }
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <DialogContentText>Am bisherigen letzten Arbeitstag wurden keine Ressourcen gefunden.</DialogContentText>
        )}
        {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={stage === "question" ? declineExtension : cancelInventory}>{stage === "question" ? "Nein" : "Abbrechen"}</Button>
        {stage === "question" ? <Button variant="contained" disabled={loading || tagNameQuery.isLoading} onClick={takeInventory}>Ja</Button> : <Button variant="contained" onClick={apply}>Übernehmen</Button>}
      </DialogActions>
    </Dialog>
  );
}
